export type FrankfurtCardPassengerField = "firstName" | "lastName";

export type FrankfurtCardPassengerFieldErrors = Record<number, Partial<Record<FrankfurtCardPassengerField, string>>>;

export type FrankfurtCardApiFieldError = {
  field?: string;
  errorMessage: string;
};

export type FrankfurtCardPassengerFieldErrorsResult = {
  byPassenger: FrankfurtCardPassengerFieldErrors;
  other: FrankfurtCardApiFieldError[];
};

// Backend echoes the estimation payload path with a 0-based index, e.g. "details.passengers[1].lastName".
// Accept snake_case too, in case the serializer naming changes.
const PASSENGER_FIELD_PATH = /^(?:details\.)?passengers\[(\d+)\]\.(firstName|lastName|first_name|last_name)$/;

const API_TO_PASSENGER_FIELD: Record<string, FrankfurtCardPassengerField> = {
  firstName: "firstName",
  lastName: "lastName",
  first_name: "firstName",
  last_name: "lastName"
};

/**
 * Maps a 400 estimation error body (`{ code, message, fields: [{ field, errorMessage }] }`) onto passenger rows.
 * Errors that can't be tied to an existing passenger input end up in `other`, so nothing is lost.
 */
export function getPassengerFieldErrors(
  prebookingError: any,
  passengerCount: number
): FrankfurtCardPassengerFieldErrorsResult {
  const result: FrankfurtCardPassengerFieldErrorsResult = { byPassenger: {}, other: [] };
  const fields = prebookingError?.fields;

  if (Number(prebookingError?.code) !== 400 || !Array.isArray(fields)) {
    return result;
  }

  fields.forEach((fieldError: any) => {
    const errorMessage = typeof fieldError?.errorMessage === "string" ? fieldError.errorMessage.trim() : "";
    if (!errorMessage) {
      return;
    }

    const path = typeof fieldError?.field === "string" ? fieldError.field.trim() : "";
    const match = PASSENGER_FIELD_PATH.exec(path);
    const index = match ? Number(match[1]) : -1;

    if (!match || index >= passengerCount) {
      result.other.push({ field: path || undefined, errorMessage });
      return;
    }

    const field = API_TO_PASSENGER_FIELD[match[2]];
    const passengerErrors = result.byPassenger[index] ?? {};
    const existingMessage = passengerErrors[field];

    passengerErrors[field] = existingMessage ? `${existingMessage} ${errorMessage}` : errorMessage;
    result.byPassenger[index] = passengerErrors;
  });

  return result;
}
